import { ComponentPortal } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import { Component, Inject, inject, Injector, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { AccountGroupService, ProjectionFilter } from '@galaxy/account-group';
import { COMPANY_PROFILE_CARD_DATA_TOKEN, SidePanelContentService } from '@galaxy/crm/static';
import { WhitelabelService } from '@galaxy/partner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { SubscriptionList } from '@vendasta/rx-utils';
import { Pipeline, PreferredCurrencyModule, USER_PIPELINES_TOKEN } from '@vendasta/sales-common';
import {
  ListOpportunitiesFilters,
  ListOpportunitiesRequestSortField,
  ListOpportunitiesResponse,
  Opportunity,
  SalesOpportunitiesSdk,
  SalesOpportunitiesService,
  SortDirection,
  StageInterface,
} from '@vendasta/sales-opportunities';
import {
  CreateOpportunityComponent,
  CreateOpportunityPageData,
  CreateOpportunityService,
  MARKET_ID_OVERRIDE_TOKEN,
  OpportunityBusinessService,
  OpportunityDetailsComponent,
  OpportunityService,
  OpportunitySidePanelEventsService,
  PipelineSwitcherService,
} from '@vendasta/sales-ui';
import { BehaviorSubject, combineLatest, iif, Observable, of, tap } from 'rxjs';
import { distinctUntilChanged, map, switchMap, take } from 'rxjs/operators';
import { CompanyAccountService } from '../../../company-account.service';
import { MatChipsModule, MatChipListboxChange } from '@angular/material/chips';

export const defaultCurrency = 'USD';

@Component({
  selector: 'app-opportunities',
  standalone: true,
  imports: [
    CommonModule,
    MatExpansionModule,
    TranslateModule,
    GalaxyLoadingSpinnerModule,
    MatCardModule,
    MatButtonModule,
    PreferredCurrencyModule,
    GalaxyBadgeModule,
    GalaxyTooltipModule,
    MatChipsModule,
  ],
  templateUrl: './opportunities.component.html',
  styleUrls: ['../association-panel-base-styles.component.scss', './opportunities.component.scss'],
})
export class OpportunitiesComponent implements OnInit {
  private readonly data = inject(COMPANY_PROFILE_CARD_DATA_TOKEN);
  private readonly opportunitiesPerPage = 5;
  private readonly subscriptions = SubscriptionList.new();
  protected hasMoreOpportunities$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  protected opportunities$: Observable<OpportunityDisplay[]>;
  protected nextOpportunitiesCursor$$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  protected opportunityCursor: string;
  protected opportunityList: OpportunityDisplay[] = [];
  protected currency$: Observable<string>;
  protected creatingAccountGroup$: Observable<boolean>;
  protected createBtnDisabled$: Observable<boolean>;
  protected pipelineStageFilter$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([
    'open',
    'closed-won',
    'closed-lost',
  ]);
  protected accountGroupId$ = this.accountCreationService.accountGroupId$;
  private marketId$ = this.accountGroupId$.pipe(
    switchMap((id) => {
      const emptyMarket$ = of('');
      const accountGroupMarket$ = this.accountGroupService
        .get(
          id,
          new ProjectionFilter({
            napData: true,
            accountGroupExternalIdentifiers: true,
          }),
        )
        .pipe(map((ag) => ag?.externalIdentifiers?.marketId ?? 'default'));

      return iif(() => id === '', emptyMarket$, accountGroupMarket$);
    }),
  );

  constructor(
    @Inject(SalesOpportunitiesService) private readonly opportunityApi: SalesOpportunitiesSdk,
    private readonly whitelabelService: WhitelabelService,
    @Inject('PARTNER_ID') private readonly partnerId$: Observable<string>,
    private readonly content: SidePanelContentService,
    private readonly opportunityService: OpportunityService,
    private readonly sidePanelEvents: OpportunitySidePanelEventsService,
    private readonly accountCreationService: CompanyAccountService,
    private readonly businessService: OpportunityBusinessService,
    private readonly createOpportunityService: CreateOpportunityService,
    private readonly accountGroupService: AccountGroupService,
    private translate: TranslateService,
    @Inject(USER_PIPELINES_TOKEN) protected readonly userPipelines$: Observable<Pipeline[]>,
    private readonly snackbarService: SnackbarService,
    private pipelineSwitcherService: PipelineSwitcherService,
  ) {
    this.accountCreationService.setAccountGroupId(this.data.account_group_id ?? '');
  }

  ngOnInit(): void {
    this.createBtnDisabled$ = this.opportunityService.hasSalespersonAccess$().pipe(map((sp) => !sp));
    this.setupOpportunityFetching();

    this.subscriptions.add(this.sidePanelEvents.sidePanelCloseEvents$, (_) => this.content.clearContent());
    this.creatingAccountGroup$ = this.accountCreationService.createAccountGroupLoading$(this.data.company_id);
  }

  private setupOpportunityFetching(): void {
    const sortOption = { field: ListOpportunitiesRequestSortField.LAST_ACTIVITY, direction: SortDirection.DESCENDING };

    this.currency$ = combineLatest([this.partnerId$, this.marketId$]).pipe(
      distinctUntilChanged(),
      switchMap(([partnerId, marketId]) => this.whitelabelService.getConfiguration(partnerId, marketId)),
      map((config) => {
        return config?.salesConfiguration?.stDefaultDisplayCurrency || defaultCurrency;
      }),
    );

    const listOpportunitiesResponse$ = combineLatest([
      this.partnerId$,
      this.marketId$,
      this.accountGroupId$,
      this.pipelineStageFilter$,
      this.nextOpportunitiesCursor$$,
      this.sidePanelEvents.opportunityCreatedEvents$,
    ]).pipe(
      distinctUntilChanged(),
      switchMap(([partnerId, marketId, accountGroupId, pipelineStageFilter, nextCursor, _]) => {
        if (!accountGroupId || !partnerId || !marketId) {
          return of(null) as Observable<ListOpportunitiesResponse>;
        }
        const oppFilter: ListOpportunitiesFilters = {
          accountGroupId: accountGroupId,
          pipelineStages: pipelineStageFilter,
        };
        return this.opportunityApi.list(
          partnerId,
          marketId,
          oppFilter,
          sortOption,
          '',
          this.opportunitiesPerPage,
          nextCursor,
        ) as Observable<ListOpportunitiesResponse>;
      }),
    );

    this.opportunities$ = combineLatest([
      listOpportunitiesResponse$,
      this.sidePanelEvents.opportunityCreatedEvents$,
      this.accountGroupId$,
      this.userPipelines$,
    ]).pipe(
      tap(([response, , ,]) => {
        this.hasMoreOpportunities$$.next(response?.hasMore || false);
        this.opportunityCursor = response?.nextCursor || null;
      }),
      map(([response, newOpp, accountGroupID, pipelines]) => {
        if (!response?.results && newOpp?.accountGroupId !== accountGroupID) return [];
        const opps = response?.results || [];
        if (newOpp && this.opportunityMissingFromList(accountGroupID, newOpp, opps)) {
          opps.unshift(newOpp);
          this.opportunityList = opps
            .slice(0, this.opportunitiesPerPage)
            .map((opportunity) => this.opportunityToOpportunityDisplay(opportunity, pipelines));
        } else {
          this.opportunityList.push(
            ...opps.map((opportunity) => this.opportunityToOpportunityDisplay(opportunity, pipelines)),
          );
        }
        return this.opportunityList;
      }),
    );

    this.subscriptions.add(this.sidePanelEvents.opportunityCreatedEvents$, (o: Opportunity) => {
      if (o) {
        this.sidePanelEvents.sidePanelCloseRequested();
      }
    });
  }

  private opportunityMissingFromList(accountGroupID: string, opportunity: Opportunity, list: Opportunity[]): boolean {
    return (
      opportunity.accountGroupId == accountGroupID &&
      !list.find((opp) => opp.opportunityId === opportunity.opportunityId)
    );
  }

  private opportunityToOpportunityDisplay(opportunity: Opportunity, pipelines: Pipeline[]): OpportunityDisplay {
    //to avoid showing default Dec 31, 0000
    const created = opportunity.created?.getUTCFullYear() > 1900 ? opportunity.created : null;
    const lastActivity = opportunity.lastActivityDate?.getUTCFullYear() > 1900 ? opportunity.lastActivityDate : created;
    const expectedClose = opportunity.expectedCloseDate?.getUTCFullYear() > 1900 ? opportunity.expectedCloseDate : null;

    let pipelineStage: StageInterface;
    const oppPipeline = pipelines.find((p) => p.id === opportunity.pipelineId);
    if (pipelines.length >= 1 && !oppPipeline) {
      pipelineStage = pipelines[0].findStage(opportunity.probability);
    } else {
      pipelineStage = oppPipeline.findStage(opportunity.probability);
    }

    return {
      accountGroupId: opportunity.accountGroupId,
      opportunityId: opportunity.opportunityId,
      name: opportunity.name,
      expectedCloseDate: expectedClose,
      lastActivity: lastActivity,
      value: opportunity.projectedFirstYearValue,
      stage: opportunity.pipelineStage,
      pipelineStage: pipelineStage,
    };
  }

  openOpportunityDetails(o: OpportunityDisplay): void {
    this.opportunityService.loadOpportunityAndSetSelected(o.opportunityId, o.accountGroupId);
    this.businessService.loadBusinessAndSetSelected(o.accountGroupId);

    const c = new ComponentPortal(OpportunityDetailsComponent);
    this.content.setContent(c);
  }

  openCreateOpportunity(): void {
    this.accountCreationService
      .fetchAccountGroupId$(this.data.company_id)
      .pipe(
        take(1),
        switchMap((accountGroupId) => {
          if (accountGroupId) {
            return this.businessService.loadBusinessAndSetSelected(accountGroupId);
          } else {
            throw null;
          }
        }),
      )
      .subscribe({
        next: (business) => {
          if (business) {
            const pageData = {
              sourceLocation: 'company-profile',
              navigateOnSuccess: false,
            } as CreateOpportunityPageData;
            this.createOpportunityService.setCreatePageDetails(pageData);
            const injector = Injector.create({
              providers: [{ provide: MARKET_ID_OVERRIDE_TOKEN, useValue: business.marketId }],
            });
            const c = new ComponentPortal(CreateOpportunityComponent, null, injector);
            this.content.setContent(c);
          }
        },
        error: () => {
          this.snackbarService.openErrorSnack('CRM.ERROR.UNABLE_TO_CREATE_OPPORTUNITY');
        },
      });
  }

  viewMoreOpportunities(): void {
    this.accountGroupId$.pipe(take(1)).subscribe((accountGroupId) => {
      this.pipelineSwitcherService.navigateToTableWithAccountFilter(accountGroupId);
    });
  }

  view5More(): void {
    this.nextOpportunitiesCursor$$.next(this.opportunityCursor);
  }

  resetOpportunities(): void {
    this.opportunityCursor = null;
    this.opportunityList = [];
    this.nextOpportunitiesCursor$$.next(null);
  }

  getStage(opportunity: OpportunityDisplay): string {
    switch (opportunity?.stage) {
      case 'open':
        return this.translate.instant('CRM.ASSOCIATION_PANEL.OPPORTUNITIES.STAGE.OPEN');
      case 'closed-won':
        return this.translate.instant('CRM.ASSOCIATION_PANEL.OPPORTUNITIES.STAGE.CLOSED_WON');
      case 'closed-lost':
        return this.translate.instant('CRM.ASSOCIATION_PANEL.OPPORTUNITIES.STAGE.CLOSED_LOST');
      default:
        return opportunity?.stage;
    }
  }

  onStageFilterChange(event: MatChipListboxChange): void {
    const all = ['open', 'closed-won', 'closed-lost'];
    if (event.value == '' || event.value == undefined) this.pipelineStageFilter$.next(all);
    else this.pipelineStageFilter$.next([event.value]);
    this.resetOpportunities();
  }
}

class OpportunityDisplay {
  accountGroupId: string;
  opportunityId: string;
  name: string;
  value: number;
  expectedCloseDate: Date;
  lastActivity: Date;
  stage: string;
  pipelineStage: StageInterface;
}
